import {
  SimplifiedUserProduct,
  SimplifiedAdminProduct,
  GetProduct,
  CreateProduct,
  UpdateProduct,
  GetAllActiveProducts,
  GetAllProducts,
  SimplifiedAdminProductInStore,
  GetAllActiveProductsInStore,
  ProductMissingAttributesResponse,
  UpdateProductBulkRequest,
  UpdateProductBulkResult
} from "../../Models/Product";
import { PagedResult } from "../../Models/PagedResult";
import { PriceAdjustmentOperation } from "../../Enums/PriceAdjustmentOperation";
import { ProductImportResult } from "../../Models/ProductImport";
import api from "../axios";

export const productService = {
  get: async (id: string): Promise<GetProduct | null> => {
    const response = await api.get(`/product/${id}`);
    return response.data;
  },

  getCount: async (): Promise<number> => {
    const response = await api.get("/product/count");
    return response.data;
  },

  getByIds: async (ids: string[]): Promise<SimplifiedUserProduct[]> => {
    const response = await api.post("/product/by-ids", ids);
    return response.data;
  },

  getAll: async (data: GetAllProducts): Promise<PagedResult<SimplifiedAdminProduct>> => {
    const queryParams = new URLSearchParams();

    queryParams.append("page", data.page.toString());
    queryParams.append("pageSize", data.pageSize.toString());

    if (data.categoryId) {
      queryParams.append("categoryId", data.categoryId);
    }

    if (data.brandId) {
      queryParams.append("brandId", data.brandId);
    }

    if (data.modelId) {
      queryParams.append("modelId", data.modelId);
    }

    if (data.status) {
      queryParams.append("status", data.status);
    }

    if (data.search) {
      queryParams.append("search", data.search);
    }

    const response = await api.get(`/product?${queryParams.toString()}`);
    return response.data;
  },

  getAllActive: async (data: GetAllActiveProducts): Promise<PagedResult<SimplifiedUserProduct>> => {
    const queryParams = new URLSearchParams();

    queryParams.append("page", data.page.toString());
    queryParams.append("pageSize", data.pageSize.toString());

    if (data.brandId) {
      queryParams.append("brandId", data.brandId);
    }

    if (data.categoryId) {
      queryParams.append("categoryId", data.categoryId);
    }

    if (data.modelId) {
      queryParams.append("modelId", data.modelId);
    }

    if (data.search) {
      queryParams.append("search", data.search);
    }

    const response = await api.get(`/product/active?${queryParams.toString()}`);
    return response.data;
  },

  getAllForInStore: async (data: GetAllActiveProductsInStore): Promise<PagedResult<SimplifiedAdminProductInStore>> => {
    const queryParams = new URLSearchParams();

    queryParams.append("page", data.page.toString());
    queryParams.append("pageSize", data.pageSize.toString());
    
    if (data.search) {
      queryParams.append("search", data.search);
    }

    const response = await api.get(`/product/in-store?${queryParams.toString()}`);
    return response.data;
  },

  importProducts: async (file: File): Promise<ProductImportResult> => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await api.post<ProductImportResult>("/product/import", formData, {
      headers: { "Content-Type": "multipart/form-data" }
    });
    return response.data;
  },

  create: async (data: CreateProduct) => {
    const response = await api.post('/product', data);
    return response.data;
  },

  update: async (data: UpdateProduct): Promise<boolean> => {
    const response = await api.put('/product', data);
    return response.data;
  },

  updateCode: async (id: string, code: string): Promise<boolean> => {
    const response = await api.patch(`/product/${id}/code`, { code });
    return response.data;
  },

  updateName: async (id: string, name: string): Promise<boolean> => {
    const response = await api.patch(`/product/${id}/name`, { name });
    return response.data;
  },

  updatePrice: async (id: string, price: number): Promise<boolean> => {
    const response = await api.patch(`/product/${id}/price`, { price });
    return response.data;
  },

  updatePlusPrice: async (id: string, plusPrice: number | null): Promise<boolean> => {
    const response = await api.patch(`/product/${id}/plus-price`, { plusPrice });
    return response.data;
  },

  updateAvailableSizes: async (id: string, availableSizes: string[] | null): Promise<boolean> => {
    const response = await api.patch(`/product/${id}/available-sizes`, { availableSizes });
    return response.data;
  },

  updateStock: async (id: string, stockQuantity: number): Promise<boolean> => {
    const response = await api.patch(`/product/${id}/stock`, { stockQuantity });
    return response.data;
  },

  updateCategory: async (id: string, categoryId: string): Promise<boolean> => {
    const response = await api.patch(`/product/${id}/category`, { categoryId });
    return response.data;
  },

  updateBrand: async (id: string, brandId: string): Promise<boolean> => {
    const response = await api.patch(`/product/${id}/brand`, { brandId });
    return response.data;
  },

  updateModel: async (id: string, modelId: string | null): Promise<boolean> => {
    const response = await api.patch(`/product/${id}/model`, { modelId });
    return response.data;
  },

  updateFlags: async (
    id: string,
    flags: { isActive?: boolean; isMadeToOrder?: boolean }
  ): Promise<boolean> => {
    const response = await api.patch<boolean>(`/product/${id}/flags`, flags);
    return response.data;
  },

  adjustPricesByCategory: async (
    categoryId: string, 
    percentage: number, 
    operation: PriceAdjustmentOperation
  ): Promise<boolean> => {
    const response = await api.put(`/product/adjust-prices`, {
      categoryId,
      percentage,
      operation
    });
    return response.data;
  },

  bulkUpdate: async (data: UpdateProductBulkRequest): Promise<UpdateProductBulkResult> => {
    const response = await api.put("/product/bulk-update", data);
    return response.data;
  },

  getMissingAttributes: async (): Promise<ProductMissingAttributesResponse> => {
    const response = await api.get("/product/missing-attributes");
    return response.data;
  },

  notifyMe: async (productId: string): Promise<boolean> => {
    const response = await api.post(`/product/${productId}/notify`);
    return response.data;
  },

  duplicate: async (id: string): Promise<SimplifiedAdminProduct> => {
    const response = await api.post(`/product/${id}/duplicate`);
    return response.data;
  },

  softDelete: async (id: string): Promise<boolean> => {
    const response = await api.delete(`/product/${id}/soft`);
    return response.data;
  },

  delete: async (id: string): Promise<boolean> => {
    const response = await api.delete(`/product/${id}/hard`);
    return response.data;
  }
};
